<script lang="ts" setup>
import { computed } from 'vue';
import { useLanguage } from '@/composables/useLanguage';
import { useUser } from '@/login/composables/useUser';

const ENABLE_ICON = true; // Enables the warning icon for translation progress
const TRANSLATION_LIMIT_UPPER = 90; // Shows the warning icon if the translation progress is below or equal to this value
const TRANSLATION_LIMIT_LOWER = 75; // Shows the warning icon in red if the translation progress is below or equal to this value

const { updateLanguage, language, languages } = useLanguage();
const { isTranslator } = useUser();

const languageName = computed<string>((): string => {
    const locale = languages.value.find(l => l.code === language.value);
    return locale != null ? locale.name : '';
});

function setLanguage(lang: string): void {
    if (isTranslator) {
        console.info('Language switcher is disabled for translators');
        return;
    }

    void updateLanguage(lang);
}
</script>

<template>
    <div>
        <SDropdown
            icon-left="Globe"
            variant="secondary-light"
            variant-type="clear"
            :label="languageName"
            size="sm"
            :disabled="isTranslator"
        >
            <SDropdownItem
                v-for="locale in languages"
                :key="locale.code"
                :aria-label="locale.name"
                :is-active="locale.code === language"
                :disabled="locale.code === language"
                @click.prevent="setLanguage(locale.code)"
            >
                <SStack justify-content="between">
                    <SText>{{ locale.name }}</SText>
                    <SIcon
                        v-if="ENABLE_ICON && locale.progress <= TRANSLATION_LIMIT_UPPER && locale.progress > 0"
                        :tooltip="$t('system.percentIsTranslated', { count: $n(locale.progress / 100, 'percent') })"
                        icon="TriangleAlert"
                        :class="{ 'sy-icon-orange': locale.progress <= TRANSLATION_LIMIT_LOWER, 'sy-icon-blue': locale.progress > TRANSLATION_LIMIT_LOWER }"
                    />
                </SStack>
            </SDropdownItem>
        </SDropdown>
    </div>
</template>
